import React, { useRef, useState } from 'react';
import styles from './nula-link.module.css';
import { Icon, icon as iconSize, Portal } from '@nula/core-ui';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { kindIcon } from '@/lib/documents/doc-title';
import { useEntityPreview } from '@/hooks/use-entity-preview';
import { EntityPreviewCard } from '@/components/entity-preview-card/entity-preview-card';

interface NulaLinkProps {
  documentId: string;
  title: string;
}

/**
 * NulaLink — inline decorator component for document links.
 *
 * Renders an inline LINK (not a chip): link-colored text with a leading
 * kind-appropriate icon (document/canvas via kindIcon; a generic link icon when
 * the target's kind hasn't resolved yet). Clicking navigates to the target.
 * Hovering shows the shared EntityPreviewCard popover (same as the legacy
 * entity-link-preview), driven by useEntityPreview (hover delay + linger +
 * close-others).
 */
export function NulaLink({ documentId, title: initialTitle }: NulaLinkProps) {
  const navigateToEntity = useNavigateToEntity();
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Resolve the live title + kind from the workspace projection.
  const liveDoc = useWorkspaceDoc(documentId);
  const title = liveDoc?.title || initialTitle;
  // Until the target's kind is known, show a generic link icon.
  const iconName = liveDoc ? kindIcon(liveDoc.kind) : 'link';

  const {
    onMouseEnter,
    onMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
    previewData,
    isOpen,
    loading,
    close,
  } = useEntityPreview(documentId);

  const handleEnter = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left });
    onMouseEnter();
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigateToEntity(documentId);
  };

  return (
    <>
      <span
        ref={ref}
        data-nula-link={documentId}
        data-link-type="document"
        onClick={handleOpen}
        onMouseEnter={handleEnter}
        onMouseLeave={onMouseLeave}
        className={styles.documentLink}
      >
        <Icon name={iconName} size={iconSize.xs} className={styles.documentLinkIcon} />
        {title || 'Untitled'}
      </span>
      {isOpen && (
        <Portal>
          <div
            className={styles.previewCard}
            style={{ '--preview-top': `${pos.top}px`, '--preview-left': `${pos.left}px` } as React.CSSProperties}
            onMouseEnter={onCardMouseEnter}
            onMouseLeave={onCardMouseLeave}
          >
            <EntityPreviewCard
              data={previewData}
              loading={loading}
              onOpen={(id) => {
                navigateToEntity(id);
                close();
              }}
            />
          </div>
        </Portal>
      )}
    </>
  );
}
